import { useRegistrationSecurity } from './RegistrationChallenge';
import { GoogleSignIn } from './GoogleSignIn';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Check, Clock, Eye, EyeOff, History, Lock, Phone, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useAppStore } from '../../app/appStore';
import { useCountryStore } from '../../shared/country/countryStore';
import { marketCopyKey, usesPix } from '../../shared/country/marketCopy';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { login, loginOtp, precheckOtpIdentifier, sendLoginOtp } from './api';
import { useAuthStore } from './authStore';
import { CountryDetectedHint, CountryPicker } from './country/CountryPicker';
import { BrandOwl, BrandWordmark } from '../../shared/brand/BrandOwl';
import { useAuthIdentifier } from './useAuthIdentifier';
import {
  AuthLangBar,
  ErrLine,
  HintLine,
  authErrorMessage,
  errorReason,
  errorRetryAfterMs,
  formatCountdown,
  identifierOtpEligible,
  loginOtpLockoutLabel,
  maskIdentifier,
  normalizeOtpExpiresAt,
  readAuthPrefs,
  scrollAuthInputIntoView,
  writeAuthPrefs,
  AuthLegalFoot,
} from './authShared';

import { AuthEntryTabs, AuthIdentifierTabs } from './AuthEntryTabs';

type LoginMethod = 'password' | 'otp';

/**
 * Login: password or OTP, toggled by a pill tab.
 *
 * Pulled out of `AuthPage` verbatim (Task 9). `resetSuccess` /
 * `onDismissResetSuccess` are the one seam back to the shell: the "password
 * reset" banner is set by `ForgotForm`, which has already unmounted by the
 * time this screen renders it, so the flag has to live one level up.
 *
 * `initialIdentifier` / `onIdentifierChange` and `remember` /
 * `onRememberChange` are the same kind of seam, for two more pieces of state
 * that used to be shared automatically (Login, Register and Forgot were one
 * component with one `identifier` and one `remember`). Splitting into
 * separate components means each one's local state starts fresh on mount —
 * without threading these back through the shell, typing a phone number and
 * then tapping "forgot password?" or "create account" would drop it, and
 * unchecking "remember me" here would stop applying to a Register submitted
 * right after.
 */
export function LoginForm({
  onAuthenticated,
  resetSuccess,
  onDismissResetSuccess,
  initialIdentifier,
  onIdentifierChange,
  remember,
  onRememberChange,
}: {
  onAuthenticated: () => void;
  resetSuccess: boolean;
  onDismissResetSuccess: () => void;
  initialIdentifier: string;
  onIdentifierChange: (value: string) => void;
  remember: boolean;
  onRememberChange: (value: boolean) => void;
}) {
  const setRoute = useAppStore((state) => state.setRoute);
  const showToast = useAppStore((state) => state.showToast);
  const registrationSecurity = useRegistrationSecurity();
  const t = useI18nStore((state) => state.t);
  const setSession = useAuthStore((state) => state.setSession);
  const enterGuest = useAuthStore((state) => state.enterGuest);

  const [identifierType, setIdentifierType] = useState<'phone' | 'email'>(initialIdentifier.includes('@') ? 'email' : 'phone');
  const identifierDrafts = useRef({ phone: initialIdentifier.includes('@') ? '' : initialIdentifier, email: initialIdentifier.includes('@') ? initialIdentifier : '' });
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const {
    identifier,
    setIdentifier: setIdentifierField,
    mode: identifierModeValue,
    isPhone,
    isValid: identifierValid,
    hint: identifierPhoneHint,
  } = useAuthIdentifier(initialIdentifier, onIdentifierChange, identifierType);
  const [identifierFocused, setIdentifierFocused] = useState(false);
  const returningUser = useMemo(() => readAuthPrefs().remember, []);
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [otpNow, setOtpNow] = useState(() => Date.now());
  const [loginOtpSentTo, setLoginOtpSentTo] = useState('');
  const [otpPrecheck, setOtpPrecheck] = useState<'idle' | 'checking' | 'notfound'>('idle');
  const [loginOtpCooldownUntil, setLoginOtpCooldownUntil] = useState(0);
  const [loginOtpLockoutUntil, setLoginOtpLockoutUntil] = useState(0);
  const [loginOtpLockoutReason, setLoginOtpLockoutReason] = useState('');
  const country = useCountryStore((state) => state.country);
  const marketAvailable = useCountryStore(state => state.loaded && state.countries.length > 0);

  useEffect(() => {
    if (!otpExpiresAt && !loginOtpCooldownUntil && !loginOtpLockoutUntil) return undefined;
    const timer = window.setInterval(() => setOtpNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [loginOtpCooldownUntil, loginOtpLockoutUntil, otpExpiresAt]);

  const mutation = useMutation({
    mutationFn: () => login({ identifier, password }),
    onMutate: () => {
      showToast(t('authSigningIn'), 'info');
    },
    onSuccess: (data) => {
      writeAuthPrefs(identifier, remember);
      setSession({
        token: data.token,
        expiresAt: Number(data.expires_at ?? data.expiresAt ?? 0),
        sessionSecret: data.sessionSecret,
        user: data.user,
        remember,
      });
      showToast(t('authWelcome'), 'success');
      onAuthenticated();
    },
  });

  const sendLoginOtpMutation = useMutation({
    mutationFn: () => sendLoginOtp(identifier),
    onMutate: () => {
      setLoginOtpLockoutUntil(0);
      setLoginOtpLockoutReason('');
      showToast(t('authSendingOtp'), 'info');
    },
    onSuccess: (payload) => {
      setLoginOtpSentTo(identifier.trim());
      setOtpExpiresAt(normalizeOtpExpiresAt(payload));
      setLoginOtpCooldownUntil(Date.now() + 60_000);
      setOtpNow(Date.now());
      showToast(t('authOtpSent'), 'success');
    },
    onError: (error) => {
      const retryAfter = errorRetryAfterMs(error);
      if (retryAfter > 0) {
        setLoginOtpLockoutUntil(Date.now() + retryAfter);
        setLoginOtpLockoutReason(errorReason(error) || 'hourly');
      }
      showToast(t('authOtpFail'), 'error');
    },
  });

  const loginOtpMutation = useMutation({
    mutationFn: () => loginOtp({ identifier: loginOtpSentTo || identifier, otp }),
    onMutate: () => {
      showToast(t('authSigningIn'), 'info');
    },
    onSuccess: (data) => {
      writeAuthPrefs(identifier, remember);
      setSession({
        token: data.token,
        expiresAt: Number(data.expires_at ?? data.expiresAt ?? 0),
        sessionSecret: data.sessionSecret,
        user: data.user,
        remember,
      });
      showToast(t('authWelcome'), 'success');
      onAuthenticated();
    },
  });

  const otpAvailable = identifierType === 'phone' ? registrationSecurity.data?.smsEnabled : registrationSecurity.data?.emailEnabled;
  const otpEligible = !!otpAvailable && identifierValid && identifierOtpEligible(identifier, country);
  useEffect(() => { if (loginMethod === 'otp' && !otpAvailable) setLoginMethodClean('password'); }, [identifierType, otpAvailable]);
  const otpSecondsLeft = Math.max(0, Math.ceil((otpExpiresAt - otpNow) / 1000));
  const loginOtpCooldownLeft = Math.max(0, Math.ceil((loginOtpCooldownUntil - otpNow) / 1000));
  const loginOtpLockoutText = loginOtpLockoutLabel(loginOtpLockoutUntil, loginOtpLockoutReason, t);
  const otpExpiryText = otpSecondsLeft > 0 ? t('loginOtpExpiresIn').replace('%t%', formatCountdown(otpSecondsLeft)) : '';

  useEffect(() => {
    if (loginMethod !== 'otp' || !loginOtpSentTo || !otpExpiresAt) return;
    if (otpNow < otpExpiresAt) return;
    setLoginOtpSentTo('');
    setOtp('');
    setOtpExpiresAt(0);
    loginOtpMutation.reset();
  }, [loginMethod, loginOtpMutation, loginOtpSentTo, otpExpiresAt, otpNow]);

  const canSubmit = useMemo(() => {
    if (!marketAvailable || !identifier.trim()) return false;
    if (loginMethod === 'otp') return !!loginOtpSentTo && otp.length >= 4 && otp.length <= 6 && otpSecondsLeft > 0;
    return identifierValid && /^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);
  }, [marketAvailable, identifier, identifierValid, loginMethod, loginOtpSentTo, otp, otpSecondsLeft, password]);
  const primaryDisabled = loginMethod === 'otp' && !loginOtpSentTo
    ? !marketAvailable || !otpEligible || sendLoginOtpMutation.isPending || loginOtpCooldownLeft > 0 || !!loginOtpLockoutText
    : !canSubmit || mutation.isPending || loginOtpMutation.isPending;
  const identifierOtpError = loginMethod === 'otp' && !!identifier.trim() && !otpEligible;

  // Password managers autofill a saved login without firing React's onChange, so
  // the controlled state — and therefore the submit button's enabled check —
  // stays empty until the user taps something (the "gray button, click once"
  // bug). Poll the DOM briefly after the form mounts and adopt autofilled values.
  const identifierRef = useRef(identifier);
  identifierRef.current = identifier;
  useEffect(() => {
    if (loginMethod !== 'password') return undefined;
    const adopt = () => {
      const pw = document.querySelector<HTMLInputElement>('input[autocomplete="current-password"]');
      const id = document.querySelector<HTMLInputElement>('input[autocomplete="username"]');
      if (pw && pw.value) setPassword((prev) => (prev === pw.value ? prev : pw.value));
      // Only adopt an autofilled identifier when it actually differs — calling
      // the handler on every tick would spam mutation.reset() and clear errors.
      if (id && id.value && id.value !== identifierRef.current) handleIdentifierInput(id.value);
    };
    const pw = document.querySelector<HTMLInputElement>('input[autocomplete="current-password"]');
    const id = document.querySelector<HTMLInputElement>('input[autocomplete="username"]');
    const fields = [pw, id].filter(Boolean) as HTMLInputElement[];
    // Native input/change fire for autofill even when React's synthetic onChange
    // is skipped; animationstart is the CSS :autofill hook (fires the instant the
    // browser fills, regardless of timing).
    const nativeEvents = ['input', 'change', 'animationstart'];
    fields.forEach((el) => nativeEvents.forEach((ev) => el.addEventListener(ev, adopt)));
    // The first in-page tap syncs too: iOS/Keychain often fills on a tap well
    // after load, and password managers fill on focus.
    document.addEventListener('pointerdown', adopt, true);
    window.addEventListener('focus', adopt);
    // Plus a short poll to catch fill-on-load before any interaction.
    let tries = 0;
    const timer = window.setInterval(() => { adopt(); if (++tries >= 20) window.clearInterval(timer); }, 150);
    adopt();
    return () => {
      fields.forEach((el) => nativeEvents.forEach((ev) => el.removeEventListener(ev, adopt)));
      document.removeEventListener('pointerdown', adopt, true);
      window.removeEventListener('focus', adopt);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMethod, identifierType]);

  function setLoginMethodClean(nextMethod: LoginMethod) {
    setLoginMethod(nextMethod);
    setOtp('');
    mutation.reset();
    sendLoginOtpMutation.reset();
    loginOtpMutation.reset();
    setOtpExpiresAt(0);
    resetLoginOtpState();
  }

  function switchIdentifier(next: 'phone' | 'email') {
    if (next === identifierType) return;
    identifierDrafts.current[identifierType] = identifier;
    setIdentifierType(next);
    setIdentifierField(identifierDrafts.current[next], next);
    setLoginMethodClean(loginMethod);
    setOtpPrecheck('idle');
  }

  function handleIdentifierInput(value: string) {
    if (otpPrecheck !== 'idle') setOtpPrecheck('idle');
    setIdentifierField(value);
    // Legacy clears the visible error as soon as the user retypes.
    mutation.reset();
    if (loginMethod === 'otp') {
      // Legacy keeps the in-flight code, cooldown and expiry while editing —
      // submit still targets the originally-sent channel (loginOtpSentTo).
      sendLoginOtpMutation.reset();
      loginOtpMutation.reset();
    }
  }

  // First OTP send goes through the existence precheck (PM anti-abuse spec);
  // resends skip it — a code was already delivered to this identifier.
  async function requestFirstLoginOtp() {
    if (!otpEligible || sendLoginOtpMutation.isPending || loginOtpCooldownLeft > 0 || loginOtpLockoutText) return;
    setOtpPrecheck('checking');
    try {
      const check = await precheckOtpIdentifier(identifier, 'login');
      if (!check.exists) {
        setOtpPrecheck('notfound');
        return;
      }
      setOtpPrecheck('idle');
      sendLoginOtpMutation.mutate();
    } catch {
      setOtpPrecheck('idle');
      showToast(t('authOtpFail'), 'error');
    }
  }

  function resetLoginOtpState() {
    setLoginOtpSentTo('');
    setLoginOtpCooldownUntil(0);
    setLoginOtpLockoutUntil(0);
    setLoginOtpLockoutReason('');
  }

  function goToForgot() {
    mutation.reset();
    sendLoginOtpMutation.reset();
    loginOtpMutation.reset();
    setOtpExpiresAt(0);
    resetLoginOtpState();
    onDismissResetSuccess();
    setRoute('auth.forgot');
  }

  function goToRegister() {
    mutation.reset();
    sendLoginOtpMutation.reset();
    loginOtpMutation.reset();
    setOtpExpiresAt(0);
    resetLoginOtpState();
    onDismissResetSuccess();
    setOtpPrecheck('idle');
    setRoute('auth.register');
  }

  function continueAsGuest() {
    enterGuest();
    onAuthenticated();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (loginMethod === 'otp') {
      if (!loginOtpSentTo) {
        void requestFirstLoginOtp();
      } else if (canSubmit && !loginOtpMutation.isPending) loginOtpMutation.mutate();
      return;
    }
    if (canSubmit && !mutation.isPending) mutation.mutate();
  }

  return (
    <div className="auth-login-fit relative z-[1]">
      <div className="auth-login-brand">
        <div className="brand-lockup"><BrandOwl size={40} /><BrandWordmark height={26} /></div>
        <h1 className="auth-login-head">{t('brandHeadline')}</h1>
        <div className="trust-strip auth-login-trust">
          <div className="trust-pill"><span className="tp-icon"><ShieldCheck size={13} aria-hidden="true" /></span><span>{t('brandTrust1')}</span></div>
          <div className="trust-pill"><span className="tp-icon"><History size={13} aria-hidden="true" /></span><span>{t('brandTrust2')}</span></div>
          {/* The teal "P" is Pix's mark; a market that does not pay by Pix promises a withdrawal window instead, under a clock. */}
          {usesPix(country.code)
            ? <div className="trust-pill trust-pill-pix"><span className="pix-mark">P</span><span>{t(marketCopyKey('heroPix', country.code))}</span></div>
            : <div className="trust-pill"><span className="tp-icon"><Clock size={13} aria-hidden="true" /></span><span>{t(marketCopyKey('heroPix', country.code))}</span></div>}
        </div>
      </div>

      <form className="auth-card" onSubmit={submit} onFocusCapture={scrollAuthInputIntoView}>
        {resetSuccess ? (
          <div className="auth-banner-success mb-3" onClick={onDismissResetSuccess}>
            <Check size={16} aria-hidden="true" /><span className="text-[12px]">{t('authPwdReset')}</span>
          </div>
        ) : null}

        <AuthEntryTabs mode="login" onChange={goToRegister} />
        <h2 className="text-base font-bold text-ink-1 mb-3">{t(returningUser ? 'loginWelcome' : 'loginWelcomeFirst')}</h2>

        <div className="auth-login-switches">
        <div className="auth-method-toggle" role="group" aria-label={t('accessMode')} data-login-otp-toggle-react="true">
          <button type="button" className={`auth-pill tap${loginMethod === 'password' ? ' is-active' : ''}`} onClick={() => setLoginMethodClean('password')}>
            <span className="auth-pill-icon"><Lock size={14} aria-hidden="true" /></span><span>{t('loginTabPassword')}</span>
          </button>
          {otpAvailable && <button type="button" className={`auth-pill tap${loginMethod === 'otp' ? ' is-active' : ''}`} onClick={() => setLoginMethodClean('otp')}>
            <span className="auth-pill-icon"><Phone size={14} aria-hidden="true" /></span><span>{t('loginTabOtp')}</span>
          </button>}
        </div>

        <AuthIdentifierTabs mode={identifierType} onChange={switchIdentifier} />
        </div>
        <div className={`auth-phone-field${identifierOtpError || (loginMethod === 'password' && !!identifier.trim() && identifierModeValue !== 'unknown' && !identifierValid) ? ' is-error' : ''}`}>
          {isPhone ? <CountryPicker /> : null}
          <input
            className={`auth-input${isPhone ? ' auth-phone' : ''}`}
            autoComplete="username"
            type={isPhone ? 'tel' : 'email'}
            aria-label={t(isPhone ? 'registerByPhone' : 'registerByEmail')}
            inputMode={isPhone ? 'tel' : 'email'}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={254}
            value={identifier}
            onChange={(event) => handleIdentifierInput(event.target.value)}
            onFocus={() => setIdentifierFocused(true)}
            onBlur={() => setIdentifierFocused(false)}
            placeholder={t(isPhone ? 'placeholderPhone' : 'registerByEmail')}
          />
        </div>
        {isPhone ? <CountryDetectedHint /> : null}
        {identifierFocused && isPhone && !identifier.trim() ? <HintLine text={t('phoneTip')} /> : null}
        {identifierPhoneHint && loginMethod === 'password' ? <HintLine tone="amber" text={identifierPhoneHint} /> : null}
        {loginMethod === 'otp' && identifierOtpError && !identifierPhoneHint ? <HintLine tone="amber" text={t('authOtpNoUsername')} /> : null}

        {loginMethod === 'password' ? (
          <>
            <div className="auth-input-wrap mt-2">
              <input className="auth-input pr-11" autoComplete="current-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t('placeholderPwd')} />
              <button type="button" className="auth-input-eye tap" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t('hidePwd') : t('showPassword')}>{showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}</button>
            </div>
            <a className="auth-forgot-inline tap" href="#" onClick={(event) => { event.preventDefault(); goToForgot(); }}>{t('forgotPwdInline')}</a>
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
              <input type="checkbox" className="auth-checkbox" checked={remember} onChange={(event) => onRememberChange(event.target.checked)} />
              <span className="text-[12px] text-ink-2 font-medium">{t('rememberMe')}</span>
            </label>
          </>
        ) : (
          <>
            {!loginOtpSentTo ? <p className="auth-otp-help">{otpPrecheck === 'checking' ? t('otpChecking') : t('loginOtpHelp')}</p> : null}
            {otpPrecheck === 'notfound' ? (
              <div className="ds-otp-nofound" data-otp-notfound-react="true">
                <span>{t('otpNoAccount')}</span>
                <button type="button" className="tap" onClick={() => { setOtpPrecheck('idle'); goToRegister(); }}>{t('createAccountAction')} ›</button>
              </div>
            ) : null}
            {loginOtpSentTo ? <p className="text-[11px] text-ink-3 mt-1.5 ml-1">{t('otpSentTo').replace('%id%', maskIdentifier(loginOtpSentTo))}</p> : null}
            {loginOtpSentTo ? (
              <div className="flex gap-2 mt-3">
                <input className="auth-input auth-otp-code-input flex-1" autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={t('placeholderOtpCode')} />
                <button type="button" className="bg-brand-600/20 text-brand-300 font-semibold px-4 rounded-xl text-sm disabled:opacity-50 whitespace-nowrap tap" disabled={sendLoginOtpMutation.isPending || loginOtpCooldownLeft > 0 || !!loginOtpLockoutText} onClick={() => sendLoginOtpMutation.mutate()}>{loginOtpCooldownLeft > 0 ? t('resendInSec').replace('%n%', String(loginOtpCooldownLeft)) : t('loginOtpResend')}</button>
              </div>
            ) : null}
            {otpExpiryText ? <p className="text-[11px] text-ink-3 mt-1.5 ml-1">{otpExpiryText}</p> : null}
            {loginOtpLockoutText ? <p className="text-[11px] text-rose-300 mt-1.5 ml-1">{loginOtpLockoutText}</p> : null}
          </>
        )}

        {mutation.isError ? <ErrLine text={authErrorMessage(mutation.error, t, identifierModeValue, 'authError')} /> : null}
        {sendLoginOtpMutation.isError && !loginOtpLockoutText ? <ErrLine text={authErrorMessage(sendLoginOtpMutation.error, t, identifierModeValue, 'authOtpFail')} /> : null}
        {loginOtpMutation.isError ? <ErrLine text={authErrorMessage(loginOtpMutation.error, t, identifierModeValue, 'authOtpLoginFail')} /> : null}

        <button className="btn-gold-primary w-full py-3 rounded-xl mt-3 transition-all tap" type="submit" disabled={primaryDisabled}>
          {loginMethod === 'otp' && !loginOtpSentTo ? t('loginOtpSendCta') : t('login')}
        </button>

        <GoogleSignIn onAuthenticated={onAuthenticated} remember={remember} />
        <div className="text-center text-xs pt-3 mt-2 border-t border-border1/50">
          <span className="text-ink-3">{t('noAccountQ')}</span>
          <a className="text-gold-400 font-semibold ml-1 tap" href="#" onClick={(event) => { event.preventDefault(); goToRegister(); }}>{t('register')}</a>
        </div>

        <p className="text-center text-[10px] text-ink-3 mt-3 leading-relaxed">
          {t('loginConsentLine').split('{terms}')[0]}
          <a href="/termos.html" target="_blank" rel="noopener" className="text-gold-400/85 underline">{t('agreeTermsLink')}</a>
          {t('loginConsentLine').split('{terms}')[1] || ''}
        </p>
      </form>

      <div className="auth-login-foot">
        <button type="button" className="auth-guest-button w-full tap" onClick={continueAsGuest}>
          <Eye size={16} aria-hidden="true" /><span>{t('guestCta')}</span><ArrowRight size={16} className="opacity-70" aria-hidden="true" />
        </button>
        <AuthLangBar />
        <AuthLegalFoot className="auth-foot-legal" />
      </div>
    </div>
  );
}
